import { useEffect, useRef, useState } from 'react';
import { Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Field';
import { useToast } from '@/components/ui/ToastContext';
import { supabase } from '@/lib/supabaseClient';

// A video stored as a link in rxk_site_content (locale 'en'): upload an
// .mp4/.webm (up to 50 MB, the storage limit) to the rxk-content bucket, or
// paste a YouTube or video link. Saves on upload, on Save and on Remove.
const MAX = 50 * 1024 * 1024;

export function VideoField({ contentKey, label, hint, youtube = true }: { contentKey: string; label: string; hint?: string; youtube?: boolean }) {
  const { push } = useToast();
  const ref = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  const [saved, setSaved] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    supabase
      .from('rxk_site_content')
      .select('value')
      .eq('key', contentKey)
      .eq('locale', 'en')
      .maybeSingle()
      .then(({ data }) => {
        const v = (data as { value: string } | null)?.value ?? '';
        setValue(v);
        setSaved(v);
      });
  }, [contentKey]);

  const save = async (v: string) => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    const { error } = await supabase.from('rxk_site_content').upsert({ key: contentKey, locale: 'en', value: v, updated_by: user?.id ?? null }, { onConflict: 'key,locale' });
    if (error) {
      push('error', error.message);
      return false;
    }
    setValue(v);
    setSaved(v);
    push('success', v ? 'Video saved' : 'Video removed');
    return true;
  };

  const upload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^video\/(mp4|webm|quicktime)$/.test(file.type)) return push('error', 'Please choose an MP4, WebM or MOV video.');
    if (file.size > MAX) return push('error', 'The video must be smaller than 50 MB. Compress it, or paste a YouTube link instead.');
    setBusy(true);
    const ext = file.name.split('.').pop()?.toLowerCase() || 'mp4';
    const path = `videos/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const { error } = await supabase.storage.from('rxk-content').upload(path, file, { contentType: file.type });
    if (error) {
      setBusy(false);
      return push('error', error.message);
    }
    const { data } = supabase.storage.from('rxk-content').getPublicUrl(path);
    await save(data.publicUrl);
    setBusy(false);
  };

  const isFile = /\.(mp4|webm|mov)(\?|$)/i.test(value);
  return (
    <div className="border border-border-subtle rounded-lg p-3 space-y-2.5">
      <div>
        <p className="text-[11px] font-semibold text-ink">{label}</p>
        {hint && <p className="text-[10.5px] text-ink-faint leading-snug">{hint}</p>}
      </div>
      {isFile && <video src={value} className="w-full max-w-sm rounded-lg bg-black aspect-video" muted controls preload="metadata" />}
      <div className="flex flex-wrap items-center gap-2">
        <input ref={ref} type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={upload} />
        <Button size="sm" variant="secondary" onClick={() => ref.current?.click()} disabled={busy}>
          <Upload className="w-3.5 h-3.5" /> {busy ? 'Uploading…' : value ? 'Replace video' : 'Upload video'}
        </Button>
        {value && (
          <Button size="sm" variant="ghost" onClick={() => save('')}>
            <Trash2 className="w-3.5 h-3.5 text-error" /> Remove
          </Button>
        )}
      </div>
      <div className="flex gap-2">
        <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder={youtube ? 'or paste a YouTube or .mp4 link' : 'or paste an .mp4 link'} />
        <Button size="sm" onClick={() => save(value.trim())} disabled={value === saved}>
          Save
        </Button>
      </div>
    </div>
  );
}
